import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useBetaAccess } from '../hooks/useBetaAccess';
import { supabase } from '../services/supabaseClient';
import { useState, useEffect } from 'react';
import { formatPlanShortName } from '../utils/formatPlanName';
import { ExclamationTriangleIcon, InformationCircleIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function TrialBanner() {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const { betaAccess } = useBetaAccess();
  const [loading, setLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check sessionStorage for dismissed state
    return sessionStorage.getItem('betaBannerDismissed') === 'true';
  });
  
  // Store banner height for navbar offset
  useEffect(() => {
    if (!isDismissed && betaAccess.hasActiveBeta) {
      // Set CSS variable for banner height
      document.documentElement.style.setProperty('--banner-height', '52px');
    } else {
      document.documentElement.style.setProperty('--banner-height', '0px');
    }
    
    return () => {
      document.documentElement.style.setProperty('--banner-height', '0px');
    };
  }, [isDismissed, betaAccess.hasActiveBeta]);
  
  // Check for beta access first (and if not dismissed)
  if (!isDismissed && betaAccess.hasActiveBeta && betaAccess.daysRemaining !== null) {
    const daysRemaining = betaAccess.daysRemaining;
    const discountText = betaAccess.discountPercentage 
      ? `${betaAccess.discountPercentage}% off` 
      : 'an exclusive discount';
    
    // Determine banner style based on days remaining
    let bannerStyle = '';
    let IconComponent = InformationCircleIcon;
    let message = '';
    let showUrgent = false;
    
    // Consistent message format
    const accessType = betaAccess.accessCode ? 'beta' : 'trial';
    
    // Check if this is a beta access type (beta_test or public_beta)
    const isBetaAccess = betaAccess.accessType === 'beta_test' || betaAccess.accessType === 'public_beta';
    
    // Different CTA for beta vs trial
    let callToAction = '';
    if (isBetaAccess) {
      // Beta users get simplified thank you message
      if (betaAccess.discountPercentage && betaAccess.discountPercentage > 0) {
        // Keep it simple and on one line
        callToAction = `Claim your special ${betaAccess.discountPercentage}% off subscription discount before your beta expires. Thank you for participating!`;
      } else {
        callToAction = 'Subscribe now to ensure uninterrupted service!';
      }
    } else {
      // Trial users get simpler message
      callToAction = betaAccess.discountPercentage && betaAccess.discountPercentage > 0
        ? `to get ${betaAccess.discountPercentage}% off!`
        : 'to ensure uninterrupted service!';
    }
    
    if (daysRemaining > 7) {
      // Info banner (blue)
      bannerStyle = 'bg-blue-600 text-white';
      IconComponent = InformationCircleIcon;
      // For beta with discount, use simplified one-line format
      if (isBetaAccess && betaAccess.discountPercentage) {
        message = `Only ${daysRemaining} days left in beta! ${callToAction}`;
      } else if (isBetaAccess) {
        message = `Only ${daysRemaining} days left in ${accessType} – ${callToAction}`;
      } else {
        message = `Only ${daysRemaining} days left in ${accessType} – Subscribe now ${callToAction}`;
      }
    } else if (daysRemaining >= 3) {
      // Warning banner (yellow)
      bannerStyle = 'bg-yellow-500 text-white';
      IconComponent = ClockIcon;
      if (isBetaAccess && betaAccess.discountPercentage) {
        message = `Only ${daysRemaining} days left in beta! ${callToAction}`;
      } else if (isBetaAccess) {
        message = `Only ${daysRemaining} days left in ${accessType} – ${callToAction}`;
      } else {
        message = `Only ${daysRemaining} days left in ${accessType} – Subscribe now ${callToAction}`;
      }
      showUrgent = true;
    } else if (daysRemaining > 0) {
      // Urgent banner (red)
      bannerStyle = 'bg-red-600 text-white';
      IconComponent = ExclamationTriangleIcon;
      if (isBetaAccess && betaAccess.discountPercentage) {
        const dayText = daysRemaining === 1 ? 'day' : 'days';
        message = `Only ${daysRemaining} ${dayText} left in beta! ${callToAction}`;
      } else if (isBetaAccess) {
        message = `Only ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in ${accessType} – ${callToAction}`;
      } else {
        message = `Only ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in ${accessType} – Subscribe now ${callToAction}`;
      }
      showUrgent = true;
    } else {
      // Expired but might still be in grace period
      bannerStyle = 'bg-red-700 text-white';
      IconComponent = ExclamationTriangleIcon;
      if (isBetaAccess && betaAccess.discountPercentage) {
        message = `Your beta access has expired! ${callToAction}`;
      } else if (isBetaAccess) {
        message = `Your ${accessType} access has expired. ${callToAction}`;
      } else {
        message = `Your ${accessType} access has expired. Subscribe now ${callToAction}`;
      }
      showUrgent = true;
    }
    
    return (
      <div className={`${bannerStyle} text-sm fixed top-0 left-0 right-0 z-50`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center flex-1">
              <IconComponent className="h-5 w-5 mr-2 flex-shrink-0" />
              <p className="font-medium mr-3">
                {message}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/upgrade')}
                className={`inline-flex items-center px-4 py-1.5 border border-transparent text-sm font-medium rounded-md ${
                  showUrgent 
                    ? 'text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-red-600'
                    : 'text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-blue-600'
                }`}
              >
                Subscribe Now
              </button>
              <button
                onClick={() => {
                  setIsDismissed(true);
                  sessionStorage.setItem('betaBannerDismissed', 'true');
                }}
                className="ml-2 p-1 rounded-md hover:bg-white/20 transition-colors"
                aria-label="Dismiss banner"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Fall back to trial banner logic
  const showTrialBanner = subscription.is_trial;

  if (!showTrialBanner) return null;

  // Debug logging for trial banner
  console.log('TrialBanner - subscription:', subscription);

  const handleEndTrialNow = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('end-trial-early');

      if (error) throw error;

      alert(`Your trial has been ended and your card will be charged shortly. You now have full access to all ${formatPlanShortName(subscription.subscription_plan)} features!`);
      
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Error ending trial early:', error);
      alert('Failed to end trial early. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate trial days remaining if trial_end_date exists
  let trialDaysRemaining = null;
  if (subscription.trial_end_date) {
    const trialEnd = new Date(subscription.trial_end_date);
    const now = new Date();
    trialDaysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }
  
  return (
    <div className="bg-blue-600 text-white text-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center">
            <InformationCircleIcon className="h-5 w-5 mr-2" />
            <p className="font-medium mr-3">
              {subscription.subscription_plan ? (
                <>Free 7-day trial of {formatPlanShortName(subscription.subscription_plan)} Plan</>
              ) : (
                <>Free 7-day trial</>
              )}
              {trialDaysRemaining !== null && trialDaysRemaining > 0 && (
                <span className="ml-1">
                  ({trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'} remaining)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => navigate('/upgrade')}
            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-blue-600"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    </div>
  );
}